import styles from "./search-results.module.less";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import LinkList from "../../link-list/link-list";
import { TLink, TLinkList } from "../../../../types";
import Link from "../../link/link";
import { useCmsPageQuery } from "../../../../react-query-queries/use-cms-page-query";
import { useEffect } from "react";
import { TCmsPageData, TStore } from "../../../../types/store-types";
import { addTrailingSlash } from "../../../../helpers/url-helper";
import { BreadcrumbsList } from "../../breadcrumbs-list/breadcrumbs-list";

const ResultsSkeletonLoader = () => {
  return (
    <SkeletonTheme
      borderRadius={"var(--core-border-radius)"}
      baseColor={"var(--ctx-skeleton-loader-background"}
      highlightColor={"var(--ctx-skeleton-loader-highlights)"}
    >
      <Skeleton duration={1.8} className={styles.skeletonRow} />
      <Skeleton duration={1.7} className={styles.skeletonRow} />
      <Skeleton duration={1.6} className={styles.skeletonRow} />
      <Skeleton duration={1.5} className={styles.skeletonRow} />
    </SkeletonTheme>
  );
};

type TSearchResultsWithBreadcrumbs = {
  breadcrumbs: Array<Partial<TLink>> | undefined;
} & TSearchResult;

type TResultList = {
  results: Array<TSearchResultsWithBreadcrumbs>;
  currentPageUrl: string;
  closeSearchFunc?: () => void;
};

const ResultList = ({ results, currentPageUrl, closeSearchFunc }: TResultList) => {
  return (
    <ul className={styles.results}>
      {results?.map((result, index) => (
        <li key={index}>
          <Link
            currentPageUrl={currentPageUrl}
            onClickCurrentPageLink={closeSearchFunc ? closeSearchFunc : undefined}
            className={styles.result}
            url={result.url}
            key={result.url}
          >
            <div className={styles.textWrapper}>
              {result.breadcrumbs && result.breadcrumbs.length > 0 && (
                <div className={styles.breadcrumbsWrapper}>
                  <BreadcrumbsList items={result.breadcrumbs} />{" "}
                </div>
              )}
              <p className={["h5", styles.title].join(" ")}>{result.title}</p>
              {result.text && <div className={["body-s", styles.text].join(" ")}>{result.text}</div>}
            </div>
            <span className={[styles.icon, "ui-icon-chevron-right"].join(" ")} />
          </Link>
        </li>
      ))}
    </ul>
  );
};

const RecommendedLinksList = ({ recommendedLinks }: { recommendedLinks: Array<TLinkList> }) => {
  return (
    <div className={styles.recommendedLinksWrapper}>
      {recommendedLinks?.map((recommendedLink, index) => (
        <LinkList key={index} title={recommendedLink.title} links={recommendedLink.links} />
      ))}
    </div>
  );
};

export type TSearchResult = {
  title: string;
  url: string;
  text?: React.ReactNode;
};

export type TRecommendedLink = {
  title: string;
  links: Array<TLink>;
};

export type TSearchResultsProps = {
  isOpen: boolean;
  isLoading: boolean;
  results: Array<TSearchResult> | undefined | null;
  recommendedLinks?: Array<TLinkList>;
  currentPageUrl: string;
  closeSearchFunc?: () => void;
};

const addBreadcrumbsToSearchResults = (
  results: Array<TSearchResult>,
  cmsPageDataStore: TStore<TCmsPageData>
): Array<TSearchResultsWithBreadcrumbs> => {
  if (!cmsPageDataStore || !cmsPageDataStore.data) {
    return results.map((result) => ({ ...result, breadcrumbs: undefined }));
  }

  const resultsWithBreadcrumbs = results.map((result) => {
    const cmsPage = cmsPageDataStore.data?.find((page) => page.Url === addTrailingSlash(result.url));

    const convertedBreacrumbs = cmsPage?.Breadcrumbs?.map((breadcrumb) => ({
      title: breadcrumb.Name,
    }));

    const breadcrumbsWihthoutCurrentPage = convertedBreacrumbs?.filter(
      (breadcrumb, index) => index !== convertedBreacrumbs.length - 1
    );

    return {
      ...result,
      breadcrumbs: breadcrumbsWihthoutCurrentPage ?? undefined,
    };
  });

  return resultsWithBreadcrumbs;
};

const SearchResults = ({
  isOpen,
  isLoading,
  results,
  recommendedLinks,
  currentPageUrl,
  closeSearchFunc,
}: TSearchResultsProps) => {
  const { data: cmsPageDataStore, refetch: getCmsPageData } = useCmsPageQuery();

  useEffect(() => {
    if (isOpen === true) {
      getCmsPageData();
    }
  }, [isOpen]);

  const resultsWithBreadcrumbs = addBreadcrumbsToSearchResults(results ?? [], cmsPageDataStore);

  return (
    <div className={styles.wrapper}>
      {isLoading ? (
        <ResultsSkeletonLoader />
      ) : results ? (
        <ResultList
          results={resultsWithBreadcrumbs}
          currentPageUrl={currentPageUrl}
          closeSearchFunc={closeSearchFunc}
        />
      ) : (
        <RecommendedLinksList recommendedLinks={recommendedLinks ?? []} />
      )}
    </div>
  );
};

export default SearchResults;
